import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useMatch } from "react-router-dom";
import Constants from "../../../config/Constants";
import PageContent from "components/PageContent";
import Box from "@mui/material/Box";
import Grid from "components/primitives/Grid";
import GroupForm from "../../Forms/GroupForm";
import { useInfoMessage } from "../../../hooks";
import useAllAthletes from "hooks/useAllAthletes";
import Mui5 from "components/mui5";
import { useTranslation } from "react-i18next";
import useGroups from "providers/GroupsProvider/useGroups";
import { useGroupActions } from "providers/GroupsProvider";
import { Group } from "types/groups";
import GroupDeleteAction from "./GroupDeleteAction";

function EditGroup() {
  const groupId = useMatch("/groups/:id/edit")?.params?.id;
  const groups = useGroups();
  const groupActions = useGroupActions();
  const athletes = useAllAthletes();
  const infoMessage = useInfoMessage({ type: "error" });
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleGroupSave = useCallback(
    (groupInfo: Group) => {
      if (!groupId) return;

      return groupActions
        .update(groupId, groupInfo)
        .then(() => {
          navigate(`${Constants.routes.groups.route}/${groupId}`);
        })
        .catch(infoMessage);
    },
    [groupActions, groupId, infoMessage, navigate]
  );

  const group = useMemo(() => {
    const group = groups.find((group) => group.id === groupId);
    if (!group || !groupId) return null;

    const { name, colour, description, athleteIds, id } = group;

    return {
      id,
      name,
      colour,
      description,
      athleteIds: athletes.filter((athlete) => athleteIds.includes(athlete.id)),
    };
  }, [athletes, groupId, groups]);

  useEffect(() => {
    if (!groupId) return;
    groupActions.fetchById(groupId);
  }, [groupActions, groupId]);

  // TODO: 404 page if group not found
  if (!groupId || !group) {
    return null;
  }

  return (
    <PageContent
      pageHeadingLink={`${Constants.routes.groups.route}/${groupId}`}
      pageHeading={t(Constants.routes.groupsEdit.titleKey)}
    >
      <Grid container justifyContent="center" sx={{ width: "100%" }}>
        <Grid size={{ xs: 12, sm: 10, md: 8, lg: 6, xl: 4 }}>
          <Mui5.Paper>
            <Box sx={{ p: 4 }}>
              <GroupForm
                initialValues={group}
                submitText={t("group.overview.edit_group_page_submit_button_text")}
                onSubmit={handleGroupSave}
                athleteOptions={athletes}
              />
              <Grid container spacing={0} mt={1.5}>
                <Grid size={12}>
                  <GroupDeleteAction group={group} />
                </Grid>
              </Grid>
            </Box>
          </Mui5.Paper>
        </Grid>
      </Grid>
    </PageContent>
  );
}

export default EditGroup;
